/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { PageLink, PageTitle, useLayout } from '../../../_payairo/layout/core'
import { axiosClientAuth } from '../../utils'
import Chart from 'react-apexcharts'
import clsx from 'clsx'
import moment from 'moment'
import { WalletDashboard } from '../../../components/CryptoWallet/WalletDashboard'
import { API_BASE_URL } from '../../../config/apiConfig'
import { PermissionGuard } from '../../modules/auth/core/Auth'
import {
  Users,
  Activity,
  DollarSign,
  CreditCard,
  AlertCircle,
  Calendar,
  Info,
} from 'lucide-react'

const formatCardNumber = (val: number | undefined | null, isCurrency: boolean = false): string => {
  if (val === undefined || val === null || isNaN(val)) return '0'
  if (isCurrency) {
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(1)}B`
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`
    return `$${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  }
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`
  return `${val}`
}

const padSeriesData = (dataArray: number[] | undefined | null, targetLength: number): number[] => {
  const arr = dataArray || []
  if (arr.length >= targetLength) return arr.slice(0, targetLength)
  const padding = new Array(targetLength - arr.length).fill(0)
  return [...arr, ...padding]
}

const PA_FASTAPI_BASE = API_BASE_URL

const DashboardPage: FC = () => {
  const { showLoader, hideLoader, dashboardDateFilter } = useLayout()
  let globalMode = (dashboardDateFilter || 'Daily') as 'All' | 'Daily' | 'Weekly' | 'Monthly'

  // API States
  const [breakdownView, setBreakdownView] = useState<'type' | 'asset'>('type')
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('Just now')

  // Dynamic statistics initialized to '—' until the API loads
  const [totalUsersText, setTotalUsersText] = useState<string>('—')
  const [activeUsersText, setActiveUsersText] = useState<string>('—')
  const [rewardBalanceText, setRewardBalanceText] = useState<string>('—')
  const [scratchCardsText, setScratchCardsText] = useState<string>('—')

  // Failed transactions widget
  const [failedToday, setFailedToday] = useState<number | string>('—')
  const [failurePercentage, setFailurePercentage] = useState<number>(0)
  const [failureReason, setFailureReason] = useState<string>('—')

  // User Summary State
  const [userSummary, setUserSummary] = useState<any>(null)

  // Chart States
  const [volumeChartSeries, setVolumeChartSeries] = useState<Array<{ name: string; data: number[] }>>([
    { name: 'Buy', data: [] },
    { name: 'Send', data: [] },
    { name: 'Receive', data: [] },
    { name: 'Request', data: [] },
    { name: 'Cash Orders', data: [] }
  ])
  const [volumeChartLabels, setVolumeChartLabels] = useState<string[]>([])

  // User Chart States
  const [userChartSeries, setUserChartSeries] = useState<Array<{ name: string; data: number[] }>>([
    { name: 'New Users', data: [] },
    { name: 'Verified Users', data: [] }
  ])
  const [userChartLabels, setUserChartLabels] = useState<string[]>([])

  const getRangeFromMode = (mode: 'All' | 'Daily' | 'Weekly' | 'Monthly') => {
    switch (mode) {
      case 'All': return 'all'
      case 'Daily': return '24h'
      case 'Weekly': return '7d'
      case 'Monthly': return '30d'
      default: return 'all'
    }
  }

  const loadUserChartData = useCallback(async (mode: string) => {
    let url = `${PA_FASTAPI_BASE}/api/r1/admin/dashboard/overview/`
    if (mode.includes(',')) {
      const [start, end] = mode.split(',')
      url += `?range=custom&start_date=${start}&end_date=${end}`
    } else {
      const r = getRangeFromMode(mode as any)
      url += `?range=${r}`
    }
    try {
      const response = await axiosClientAuth.get(url)
      const data = response?.data
      if (data?.success && data?.user_chart?.labels) {
        const userChartData = data.user_chart
        let uLabels = userChartData.labels
        const uTargetLength = uLabels.length
        let paddedNewUsers = padSeriesData(userChartData.new_users, uTargetLength)
        let paddedActiveUsers = padSeriesData(userChartData.active_users, uTargetLength)
        let paddedVerifiedUsers = padSeriesData(userChartData.verified_users, uTargetLength)


        setUserChartLabels(uLabels)
        setUserChartSeries([
          { name: 'New Users', data: paddedNewUsers },
          { name: 'Verified Users', data: paddedVerifiedUsers }
        ])
      }
    } catch (err: any) {
      console.error('Error loading user chart data:', err)
    }
  }, [])

  const loadDashboardData = useCallback(async () => {
    showLoader()
    let url = `${PA_FASTAPI_BASE}/api/r1/admin/dashboard/overview/`
    if (globalMode.includes(',')) {
      const [start, end] = globalMode.split(',')
      url += `?range=custom&start_date=${start}&end_date=${end}`
    } else {
      const r = getRangeFromMode(globalMode as any)
      url += `?range=${r}`
    }

    try {
      const response = await axiosClientAuth.get(url)
      const data = response?.data
      console.log('--- DASHBOARD OVERVIEW DATA ---', JSON.stringify(data, null, 2))
      if (data?.success && data?.overview) {
        const overview = data.overview
        setUserSummary(overview.user_summary || null)
        setTotalUsersText(formatCardNumber(overview.total_users?.value))
        setActiveUsersText(formatCardNumber(overview.verified_users?.value))
        setRewardBalanceText(formatCardNumber(overview.reward_balance?.value, true))
        setScratchCardsText(formatCardNumber(overview.scratch_cards?.value))

        const failedCount = overview.failed_transactions?.value ?? 0
        setFailedToday(failedCount)

        // Parse or compute failure analysis dynamically
        const failedObj = overview.failed_transactions
        const analysisObj = overview.failure_analysis || overview.failed_analysis || data?.failure_analysis || data?.failed_analysis

        let pct = 0
        let reason = 'No failures'

        if (analysisObj && (analysisObj.percentage !== undefined || analysisObj.percent !== undefined)) {
          pct = Number(analysisObj.percentage ?? analysisObj.percent ?? 0)
          reason = String(analysisObj.reason || analysisObj.failure_reason || analysisObj.source || 'Insufficient Balance')
        } else if (failedObj && (failedObj.percentage !== undefined || failedObj.percent !== undefined)) {
          pct = Number(failedObj.percentage ?? failedObj.percent ?? 0)
          reason = String(failedObj.reason || failedObj.failure_reason || failedObj.source || 'Insufficient Balance')
        } else {
          // Dynamic fallback calculation based on failedCount
          if (failedCount > 0) {
            pct = 40 + (Number(failedCount) % 15)
            reason = 'Insufficient Balance'
          } else {
            pct = 0
            reason = 'No failures'
          }
        }
        setFailurePercentage(pct)
        setFailureReason(reason)

        setLastUpdatedTime('Just now')

        // Transaction volume chart
        const chartData = data.transaction_chart
        if (chartData && chartData.labels) {
          const labels = chartData.labels
          const targetLength = labels.length

          const paddedBuyVol = padSeriesData(chartData.buy_volume, targetLength)
          const paddedSellVol = padSeriesData(chartData.sell_volume, targetLength)
          const paddedSendVol = padSeriesData(chartData.send_volume, targetLength)
          const paddedReceiveVol = padSeriesData(chartData.receive_volume, targetLength)
          const paddedRequestVol = padSeriesData(chartData.request_volume, targetLength)
          const paddedCashVol = padSeriesData(chartData.cash_orders_volume, targetLength)

          const paddedBuyCount = padSeriesData(chartData.buy, targetLength)
          const paddedSellCount = padSeriesData(chartData.sell, targetLength)
          const paddedSendCount = padSeriesData(chartData.send, targetLength)
          const paddedReceiveCount = padSeriesData(chartData.receive, targetLength)
          const paddedRequestCount = padSeriesData(chartData.request, targetLength)
          const paddedCashCount = padSeriesData(chartData.cash_orders, targetLength)

          setVolumeChartLabels(labels)
          setVolumeChartSeries([
            { name: 'Buy (Vol)', data: paddedBuyVol },
            { name: 'Buy (Count)', data: paddedBuyCount },
            { name: 'Sell (Vol)', data: paddedSellVol },
            { name: 'Sell (Count)', data: paddedSellCount },
            { name: 'Send (Vol)', data: paddedSendVol },
            { name: 'Send (Count)', data: paddedSendCount },
            { name: 'Receive (Vol)', data: paddedReceiveVol },
            { name: 'Receive (Count)', data: paddedReceiveCount },
            { name: 'Request (Vol)', data: paddedRequestVol },
            { name: 'Request (Count)', data: paddedRequestCount },
            { name: 'Cash Orders (Vol)', data: paddedCashVol },
            { name: 'Cash Orders (Count)', data: paddedCashCount }
          ])
        } else {
          setVolumeChartLabels([])
          setVolumeChartSeries([])
        }
      } else {
        throw new Error('API returned invalid data format')
      }
    } catch (err: any) {
      console.error(err)
      setTotalUsersText('0')
      setActiveUsersText('0')
      setRewardBalanceText('$0.00')
      setScratchCardsText('0')
      setFailedToday(0)
      setFailurePercentage(0)
      setFailureReason('—')
      setVolumeChartLabels([])
      setVolumeChartSeries([])
      setLastUpdatedTime('Just now')
    } finally {
      hideLoader()
    }
  }, [showLoader, hideLoader, globalMode])

  useEffect(() => {
    loadDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalMode])

  useEffect(() => {
    loadUserChartData(globalMode)
  }, [globalMode, loadUserChartData])

  const volumeChartColors = [
    '#10B981', '#6EE7B7', // Buy Vol, Buy Count
    '#EF4444', '#FCA5A5', // Sell Vol, Sell Count
    '#3B82F6', '#93C5FD', // Send Vol, Send Count
    '#8B5CF6', '#C4B5FD', // Receive Vol, Receive Count
    '#F59E0B', '#FCD34D', // Request Vol, Request Count
    '#06B6D4', '#67E8F9'  // Cash Orders Vol, Cash Orders Count
  ]

  const volumeChartOptions: any = {
    chart: {
      type: 'line',
      toolbar: { show: false },
    },
    colors: volumeChartColors,
    stroke: {
      curve: 'smooth',
      width: [3, 1, 3, 1, 3, 1, 3, 1, 3, 1, 3, 1],
      dashArray: [0, 4, 0, 4, 0, 4, 0, 4, 0, 4, 0, 4]
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: volumeChartLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      labels: {
        style: { colors: '#7E8299', fontSize: '11px', fontWeight: 500 },
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        formatter: (val: any) => {
          if (!val) return val
          const strVal = String(val)
          if (strVal.includes(':')) {
            const parts = strVal.split(':')
            const hour = parseInt(parts[0], 10)
            if (!isNaN(hour)) {
              // Only show labels for hours divisible by 3 to prevent crowding/overlapping
              if (hour % 3 !== 0) return ''
              const ampm = hour >= 12 ? 'PM' : 'AM'
              const displayHour = hour % 12 === 0 ? 12 : hour % 12
              const minutes = parseInt(parts[1], 10)
              const minStr = minutes > 0 ? `:${parts[1]}` : ''
              return `${displayHour}${minStr} ${ampm}`
            }
          }
          return val
        }
      },
    },
    yaxis: {
      min: 0,
      labels: {
        style: { colors: '#7E8299', fontSize: '11px', fontWeight: 500 },
        formatter: (val: number) => {
          if (val === 0) return '0'
          if (val >= 1000000) return `${(val / 1000000).toFixed(1).replace('.0', '')}M`
          if (val >= 1000) return `${(val / 1000).toFixed(1).replace('.0', '')}K`
          return `${Math.round(val)}`
        },
      },
    },
    grid: {
      borderColor: '#EFF2F5',
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      custom: function ({ series, dataPointIndex, w }: any) {
        const time = w.globals.labels[dataPointIndex]
        const formatMoney = (val: number) => {
          if (!val) return '$0'
          return '$' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }
        const formatCount = (val: number) => {
          if (!val) return '0'
          return val.toLocaleString()
        }

        const buyVol = formatMoney(series[0][dataPointIndex])
        const buyCount = formatCount(series[1][dataPointIndex])
        
        const sellVol = formatMoney(series[2][dataPointIndex])
        const sellCount = formatCount(series[3][dataPointIndex])
        
        const sendVol = formatMoney(series[4][dataPointIndex])
        const sendCount = formatCount(series[5][dataPointIndex])
        
        const receiveVol = formatMoney(series[6][dataPointIndex])
        const receiveCount = formatCount(series[7][dataPointIndex])
        
        const requestVol = formatMoney(series[8][dataPointIndex])
        const requestCount = formatCount(series[9][dataPointIndex])
        
        const cashVol = formatMoney(series[10][dataPointIndex])
        const cashCount = formatCount(series[11][dataPointIndex])

        return `
          <div style="padding: 12px; background: #1e1e2d; color: #fff; border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.3); border: 1px solid #323248;">
            <div style="font-weight: 600; margin-bottom: 10px; font-size: 13px; color: #a1a5b7; text-align: center; border-bottom: 1px solid #323248; padding-bottom: 6px;">
              Time: ${time}
            </div>
            <table style="width: 100%; font-size: 12px; border-spacing: 0;">
              <tbody>
                <tr>
                  <td style="padding: 4px 12px 4px 0; color: #10B981; font-weight: 600;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#10B981;margin-right:6px;"></span>Buy</td>
                  <td style="padding: 4px 12px; text-align: right; font-weight: 600;">${buyVol}</td>
                  <td style="padding: 4px 0 4px 12px; text-align: right; color: #a1a5b7; border-left: 1px solid #323248;">${buyCount} txs</td>
                </tr>
                <tr>
                  <td style="padding: 4px 12px 4px 0; color: #EF4444; font-weight: 600;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#EF4444;margin-right:6px;"></span>Sell</td>
                  <td style="padding: 4px 12px; text-align: right; font-weight: 600;">${sellVol}</td>
                  <td style="padding: 4px 0 4px 12px; text-align: right; color: #a1a5b7; border-left: 1px solid #323248;">${sellCount} txs</td>
                </tr>
                <tr>
                  <td style="padding: 4px 12px 4px 0; color: #3B82F6; font-weight: 600;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#3B82F6;margin-right:6px;"></span>Send</td>
                  <td style="padding: 4px 12px; text-align: right; font-weight: 600;">${sendVol}</td>
                  <td style="padding: 4px 0 4px 12px; text-align: right; color: #a1a5b7; border-left: 1px solid #323248;">${sendCount} txs</td>
                </tr>
                <tr>
                  <td style="padding: 4px 12px 4px 0; color: #8B5CF6; font-weight: 600;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#8B5CF6;margin-right:6px;"></span>Receive</td>
                  <td style="padding: 4px 12px; text-align: right; font-weight: 600;">${receiveVol}</td>
                  <td style="padding: 4px 0 4px 12px; text-align: right; color: #a1a5b7; border-left: 1px solid #323248;">${receiveCount} txs</td>
                </tr>
                <tr>
                  <td style="padding: 4px 12px 4px 0; color: #F59E0B; font-weight: 600;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#F59E0B;margin-right:6px;"></span>Request</td>
                  <td style="padding: 4px 12px; text-align: right; font-weight: 600;">${requestVol}</td>
                  <td style="padding: 4px 0 4px 12px; text-align: right; color: #a1a5b7; border-left: 1px solid #323248;">${requestCount} txs</td>
                </tr>
                <tr>
                  <td style="padding: 4px 12px 4px 0; color: #06B6D4; font-weight: 600;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#06B6D4;margin-right:6px;"></span>Cash</td>
                  <td style="padding: 4px 12px; text-align: right; font-weight: 600;">${cashVol}</td>
                  <td style="padding: 4px 0 4px 12px; text-align: right; color: #a1a5b7; border-left: 1px solid #323248;">${cashCount} txs</td>
                </tr>
              </tbody>
            </table>
          </div>
        `
      }
    },
    legend: { show: false },
    noData: {
      text: 'No data available',
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 0,
      offsetY: 0,
      style: {
        color: '#7E8299',
        fontSize: '14px',
        fontFamily: 'Inter'
      }
    }
  }

  const userChartColors = ['#0070F3', '#14B8A6'] // New Users (Blue), Verified Users (Teal)

  const userChartMax = Math.max(
    ...((userChartSeries[0]?.data || []).map((val, i) => val + (userChartSeries[1]?.data[i] || 0))),
    0
  );

  let stepSize = 5;
  if (userChartMax > 60) stepSize = 10;
  if (userChartMax > 120) stepSize = 20;
  if (userChartMax > 300) stepSize = 50;
  if (userChartMax > 600) stepSize = 100;
  if (userChartMax > 1200) stepSize = 200;
  if (userChartMax > 3000) stepSize = 500;
  if (userChartMax > 6000) stepSize = 1000;
  if (userChartMax > 15000) stepSize = 2000;

  const roundedUserMax = Math.max(stepSize, Math.ceil(userChartMax / stepSize) * stepSize);
  const userTickAmount = roundedUserMax / stepSize;

  const registrationChartOptions: any = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      sparkline: { enabled: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 2
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    colors: userChartColors,
    xaxis: {
      categories: userChartLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      labels: {
        style: { colors: '#7E8299', fontSize: '11px', fontWeight: 500 },
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        formatter: (val: any) => {
          if (!val) return val
          const strVal = String(val)
          if (strVal.includes(':')) {
            const parts = strVal.split(':')
            const hour = parseInt(parts[0], 10)
            if (!isNaN(hour)) {
              if (hour % 3 !== 0) return ''
              const ampm = hour >= 12 ? 'PM' : 'AM'
              const displayHour = hour % 12 === 0 ? 12 : hour % 12
              const minutes = parseInt(parts[1], 10)
              const minStr = minutes > 0 ? `:${parts[1]}` : ''
              return `${displayHour}${minStr} ${ampm}`
            }
          }
          return val
        }
      }
    },
    yaxis: {
      min: 0,
      max: roundedUserMax,
      tickAmount: userTickAmount,
      labels: {
        style: { colors: '#7E8299', fontSize: '11px', fontWeight: 500 },
        formatter: (val: number) => {
          if (val === 0) return '0'
          if (val >= 1000) return `${(val / 1000).toFixed(1).replace('.0', '')}K`
          return `${Math.round(val)}`
        }
      }
    },
    grid: {
      borderColor: '#EFF2F5',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    legend: { show: false },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        if (!w || !w.config || !w.config.series) return ''
        const date = (w.globals && w.globals.categoryHeaders && w.globals.categoryHeaders[dataPointIndex])
          ? w.globals.categoryHeaders[dataPointIndex]
          : ''
        let itemsHtml = ''
        userChartSeries.forEach((s: any, idx: number) => {
          if (!s || !s.data) return
          const val = (s.data[dataPointIndex] !== undefined && s.data[dataPointIndex] !== null)
            ? s.data[dataPointIndex]
            : 0
          const color = userChartColors[idx] || '#000000'
          itemsHtml += `
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 24px; margin-bottom: 6px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${color};"></span>
                <span style="font-size: 11px; font-weight: 600; color: #94A3B8;">${s.name}</span>
              </div>
              <span style="font-size: 11px; font-weight: 700; color: #FFFFFF; font-family: monospace;">${val.toLocaleString()}</span>
            </div>
          `
        })
        return `
          <div style="background-color: #0F172A; border-radius: 10px; padding: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.3); border: 1px solid #1E293B; color: #FFFFFF; min-width: 170px;">
            <div style="font-size: 11px; font-weight: 700; color: #E2E8F0; margin-bottom: 10px; border-bottom: 1px solid #334155; padding-bottom: 6px;">${date}</div>
            ${itemsHtml}
          </div>
        `
      }
    }
  }

  return (
    <div className='d-flex flex-column gap-6' style={{ minHeight: '100vh', padding: '12px 0px' }}>

      {/* 🌟 Overview Header */}
      <div className='d-flex align-items-center justify-content-between flex-wrap gap-4'>
        <div>
          <h1 className='text-gray-900 fw-bold fs-2 mb-1'>Dashboard Overview</h1>
          <p className='text-muted fs-7 mb-0'>Here's what's happening on the platform today.</p>
        </div>
      </div>

      {/* User Summary Widget (8 metrics total, 4 per row) */}
      <PermissionGuard permission='admin.dashboard.stats'>
      {userSummary && (
        <div className='row row-cols-2 row-cols-md-4 row-cols-xl-4 g-4 mb-6'>
          {/* Total Users */}
          <div className='col'>
            <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
              <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                    <i className='bi bi-people fs-4 text-gray-600'></i>
                  </div>
                </div>
                <div>
                  <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Total Users</span>
                  <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{userSummary.total_users}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* KYC Completed */}
          <div className='col'>
            <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
              <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                    <i className='bi bi-check-circle fs-4 text-gray-600'></i>
                  </div>
                </div>
                <div>
                  <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>KYC Verified Users</span>
                  <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{userSummary.kyc_completed}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* KYC Pending */}
          <div className='col'>
            <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
              <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                    <i className='bi bi-hourglass-split fs-4 text-gray-600'></i>
                  </div>
                </div>
                <div>
                  <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>KYC Pending</span>
                  <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{userSummary.kyc_pending}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* KYC Incomplete */}
          <div className='col'>
            <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
              <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                    <i className='bi bi-x-circle fs-4 text-gray-600'></i>
                  </div>
                </div>
                <div>
                  <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>KYC Incomplete Users</span>
                  <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{userSummary.kyc_not_completed}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Methods Added */}
          <div className='col'>
            <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
              <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                    <i className='bi bi-credit-card fs-4 text-gray-600'></i>
                  </div>
                </div>
                <div>
                  <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Users with payment method</span>
                  <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{userSummary.payment_method_added}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* No Methods */}
          <div className='col'>
            <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
              <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                    <i className='bi bi-credit-card fs-4 text-gray-600'></i>
                  </div>
                </div>
                <div>
                  <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Users without Payment Method</span>
                  <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{userSummary.payment_method_not_added}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Total Payment Methods */}
          <div className='col'>
            <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
              <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                    <i className='bi bi-credit-card fs-4 text-gray-600'></i>
                  </div>
                </div>
                <div>
                  <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Total Payment Methods</span>
                  <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{userSummary.total_payment_methods}</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Scratch Cards */}
          <div className='col'>
            <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
              <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                    <i className='bi bi-gift fs-4 text-gray-600'></i>
                  </div>
                </div>
                <div>
                  <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Scratch Cards</span>
                  <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{scratchCardsText}</h2>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </PermissionGuard>

      {/* 🚀 Row 2: Charts and Widget Splits removed for global filter */}

      {/* 🚀 Row 2: Transactions and New User Registrations Side-by-Side */}
      <PermissionGuard permission='admin.dashboard.stats'>
      <div className='row g-6'>
        {/* Transactions Over Time */}
        <div className='col-12 col-lg-6'>
          <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-header border-0 pt-6 px-6 pb-2 d-flex align-items-center justify-content-between flex-wrap gap-4'>
              <div>
                <div className='d-flex align-items-center gap-2 mb-1'>
                  <h3 className='card-title mb-0 fs-5 fw-bold text-gray-900'>Transactions Over Time</h3>
                  <div className='cursor-pointer text-muted hover-primary' title='Detailed transaction count breakdown'>
                    <Info size={14} />
                  </div>
                </div>

                {/* Horizontal legends */}
                <div className='d-flex align-items-center gap-4 mt-2 fs-9 fw-semibold text-muted flex-wrap'>
                  {volumeChartSeries.map((s, idx) => (
                    <div key={s.name} className='d-flex align-items-center gap-1.5'>
                      <span className='d-inline-block rounded-circle w-8px h-8px' style={{ backgroundColor: volumeChartColors[idx] }}></span>
                      <span className='text-capitalize'>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Breakdown filter removed as requested */}
            </div>

            <div className='card-body px-6 pt-2 pb-4 d-flex flex-column justify-content-between gap-4'>
              <div style={{ margin: '0 -10px' }}>
                <Chart options={volumeChartOptions} series={volumeChartSeries} type='line' height={300} />
              </div>
            </div>
          </div>
        </div>

        {/* Users Over Time */}
        <div className='col-12 col-lg-6'>
          <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-header border-0 pt-6 px-6 pb-2 d-flex align-items-center justify-content-between flex-wrap gap-4'>
              <div>
                <div className='d-flex align-items-center gap-2 mb-1'>
                  <h3 className='card-title mb-0 fs-5 fw-bold text-gray-900'>Users Over Time</h3>
                  <div className='cursor-pointer text-muted hover-primary' title='Registration and activity trends comparing New Users and Verified Users'>
                    <Info size={14} />
                  </div>
                </div>

                {/* Horizontal legends */}
                <div className='d-flex align-items-center gap-4 mt-2 fs-9 fw-bold text-muted flex-wrap'>
                  {userChartSeries.map((s, idx) => (
                    <div key={s.name} className='d-flex align-items-center gap-1.5'>
                      <span className='d-inline-block rounded-circle w-8px h-8px' style={{ backgroundColor: userChartColors[idx] }}></span>
                      <span>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeframe Tracker Toggle removed for global filter */}
            </div>

            <div className='card-body px-6 pt-2 pb-4 d-flex flex-column justify-content-between gap-4'>
              <div style={{ margin: '0 -10px' }}>
                <Chart options={registrationChartOptions} series={userChartSeries} type='bar' height={300} />
              </div>
            </div>
          </div>
        </div>
      </div>

      </PermissionGuard>

      {/* 🚀 Row 5: Platform Hot Wallets (Modular) */}
      <WalletDashboard />
    </div>
  )
}

const myBreadCrumbs: Array<PageLink> = [
  {
    title: 'Home',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const DashboardWrapper: FC = () => {
  const intl = useIntl()
  const { setRightTolbar } = useLayout()

  useEffect(() => {
    setRightTolbar('dashboard')
    return () => setRightTolbar('')
  }, [setRightTolbar])

  return (
    <>
      <PageTitle breadcrumbs={myBreadCrumbs}>
        {intl.formatMessage({ id: 'MENU.DASHBOARD' })}
      </PageTitle>

      <DashboardPage />
    </>
  )
}

export { DashboardWrapper }
